import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { routeTree } from './routeTree.gen';
import { queryClient } from './lib/api';
import { authStore } from './lib/auth-store';
import { env } from './lib/env';
import { applyTheme, readThemeChoice } from './lib/theme';
import { warmOrigins } from './lib/warm-origins';
import { RouteError, RouteNotFound, RoutePending } from './components/route-states';
import './styles.css';

const router = createRouter({
  routeTree,
  // Served under a path rather than a domain of its own, every route sits below it.
  // BASE_URL always ends in a slash; the router wants the path without one.
  basepath: import.meta.env.BASE_URL.replace(/\/+$/, '') || undefined,
  context: { queryClient, auth: authStore },
  defaultPreload: 'intent',
  defaultPendingComponent: RoutePending,
  defaultErrorComponent: RouteError,
  defaultNotFoundComponent: RouteNotFound,
  defaultPendingMs: 200,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

async function bootstrap() {
  // index.html stamps the theme before paint. This is the belt and braces pass,
  // for the case where that script did not run.
  applyTheme(readThemeChoice());

  // First thing, before any of it is asked for: somebody arriving from the portal needs the
  // API and Firebase within a moment, and each unopened connection costs ~700ms.
  if (!env.mock) {
    warmOrigins({ apiBaseUrl: env.apiBaseUrl, firebase: env.firebaseConfigured });
  }

  if (env.mock) {
    const { startMockApi } = await import('./mocks/browser');
    await startMockApi();
  }
  await authStore.start();

  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('No #root element in the page');

  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
